import React from 'react';
import { Container } from 'react-bootstrap';
import '../common-styles.css';
import '../shared/GradientHero.css';
import GradientHero from '../shared/GradientHero';
import './GradingSystemsPage.css';

import imgD from '../../assets/images/colors/D.png';
import imgE from '../../assets/images/colors/E.png';
import imgF from '../../assets/images/colors/F.png';
import imgG from '../../assets/images/colors/G.png';
import imgH from '../../assets/images/colors/H.png';
import imgI from '../../assets/images/colors/I.png';
import imgJ from '../../assets/images/colors/J.png';
import imgKM from '../../assets/images/colors/K-M.png';
import imgNR from '../../assets/images/colors/N-R.png';
import imgSZ from '../../assets/images/colors/S-Z.png';
import imgFancy from '../../assets/images/colors/FANCY.png';

// --- Matrix Table Data Helpers ---
interface GradeMatrix { code: string; desc: string; }

// Clarity individual rows (vertical table like image)
const CLARITY_ROWS: GradeMatrix[] = [
  { code: 'FL', desc: 'Flawless' },
  { code: 'IF', desc: 'Internally Flawless' },
  { code: 'VVS1', desc: 'Very Very Slightly Included-1' },
  { code: 'VVS2', desc: 'Very Very Slightly Included-2' },
  { code: 'VS1', desc: 'Very Slightly Included-1' },
  { code: 'VS2', desc: 'Very Slightly Included-2' },
  { code: 'SI1', desc: 'Slightly Included-1' },
  { code: 'SI2', desc: 'Slightly Included-2' },
  { code: 'SI3', desc: 'Slightly Included-3' },
  { code: 'I1', desc: 'Included-1' }
];

// Color groups displayed exactly like the image (no dashes – use spaces / word connectors)
const COLOR_ROWS: GradeMatrix[] = [
  { code: 'D E F', desc: 'Colorless' },
  { code: 'G H I J', desc: 'Near Colorless' },
  { code: 'K L M', desc: 'Faint' },
  { code: 'N to R', desc: 'Very Light' },
  { code: 'S to Z', desc: 'Light' }
];

// Horizontal (matrix) grading sets
const CUT_MATRIX: GradeMatrix[] = [
  { code: 'EX', desc: 'Excellent Cut' },
  { code: 'VG', desc: 'Very Good Cut' },
  { code: 'G', desc: 'Good Cut' },
  { code: 'F', desc: 'Fair Cut' }
];

const POLISH_MATRIX: GradeMatrix[] = [
  { code: 'EX', desc: 'Excellent Polish' },
  { code: 'VG', desc: 'Very Good Polish' },
  { code: 'G', desc: 'Good Polish' },
  { code: 'F', desc: 'Fair Polish' }
];

const SYMMETRY_MATRIX: GradeMatrix[] = [
  { code: 'EX', desc: 'Excellent Symmetry' },
  { code: 'VG', desc: 'Very Good Symmetry' },
  { code: 'G', desc: 'Good Symmetry' },
  { code: 'F', desc: 'Fair Symmetry' }
];

const HA_MATRIX: GradeMatrix[] = [
  { code: 'N', desc: 'None' },
  { code: 'EX', desc: 'Excellent H&A' },
  { code: 'VG', desc: 'Very Good H&A' },
  { code: 'GOOD', desc: 'GOOD H&A' },
  { code: 'F', desc: 'Fair H&A' }
];

const LUSTER_MATRIX: GradeMatrix[] = [
  { code: 'EX', desc: 'Excellent Luster' },
  { code: 'VG', desc: 'Very Good Luster' },
  { code: 'G', desc: 'Good Luster' }
];
const MILKY_MATRIX: GradeMatrix[] = [
  { code: 'ML1', desc: 'Slightly Milky' },
  { code: 'ML2', desc: 'Medium Milky' },
  { code: 'ML3', desc: 'Heavy Milky' }
];

const FLUORESCENCE_MATRIX: GradeMatrix[] = [
  { code: 'N', desc: 'None Fluorescence' },
  { code: 'FNT', desc: 'Faint Fluorescence' },
  { code: 'MED', desc: 'Medium Fluorescence' },
  { code: 'STG', desc: 'Strong Fluorescence' },
  { code: 'VST', desc: 'Very Strong Fluorescence' }
];

// Inclusion related matrices (white / black / open / extra facet) match image pattern
const WHITE_INCLUSION_MATRIX: GradeMatrix[] = [
  { code: 'N', desc: 'None' },
  { code: 'PP1', desc: 'Pin Point Minor Inclusion' },
  { code: 'PP2', desc: 'Pin Point Medium Inclusion' },
  { code: 'CLR1', desc: 'Crystal Minor Inclusion' },
  { code: 'CLR2', desc: 'Crystal Medium Inclusion' },
  { code: 'SPR1', desc: 'Spreadead Minor Inclusion' },
  { code: 'SPR2', desc: 'Spreadead Medium Inclusion' },
  { code: 'SPR3', desc: 'Spreadead Heavy Inclusion' },
  { code: 'FR1', desc: 'Feather Minor Inclusion' },
  { code: 'FR2', desc: 'Feather Medium Inclusion' },
  { code: 'FR3', desc: 'Feather Heavy Inclusion' },
  { code: 'CLD1', desc: 'Cloud Minor Inclusion' },
  { code: 'CLD2', desc: 'Cloud Medium Inclusion' },
  { code: 'CLD3', desc: 'Cloud Heavy Inclusion' }
];

const BLACK_INCLUSION_MATRIX: GradeMatrix[] = [
  { code: 'N', desc: 'None' },
  { code: 'BPP', desc: 'Pin Point Black Inclusion' },
  { code: 'BI', desc: 'Minor Black Inclusion' },
  { code: 'B2', desc: 'Medium Black Inclusion' }
];

const OPEN_INCLUSION_MATRIX: GradeMatrix[] = [
  { code: 'N', desc: 'None' },
  { code: 'HL', desc: 'Hair Line Open Inclusion' },
  { code: 'SM', desc: 'Small Open Inclusion' },
  { code: 'MD', desc: 'Medium Open Inclusion' },
  { code: 'HV', desc: 'Heavy Open Inclusion' }
];

const EXTRA_FACET_MATRIX: GradeMatrix[] = [
  { code: 'N', desc: 'None' },
  { code: 'SM', desc: 'Small Extra Facet' },
  { code: 'MD', desc: 'Medium Extra Facet' },
  { code: 'HV', desc: 'Heavy Extra Facet' }
];

// Utility renderer for horizontal matrix (two-row) tables used across sections
const MatrixTable: React.FC<{ title: string; grades: GradeMatrix[]; ariaLabel?: string; descLabel?: string; }> = ({ title, grades, ariaLabel, descLabel }) => (
  <table className="grading-table matrix-table" aria-label={ariaLabel || title}>
    <thead>
      <tr>
        <th className="first-col" scope="col">{title}</th>
        {grades.map(g => <th key={g.code} scope="col">{g.code}</th>)}
      </tr>
    </thead>
    <tbody>
      <tr>
        <th className="first-col" scope="row">{descLabel || 'Description'}</th>
        {grades.map(g => <td key={g.code + '-d'}>{g.desc}</td>)}
      </tr>
    </tbody>
  </table>
);

const colorImageGroups = [
  { img: imgD, label: 'D' },
  { img: imgE, label: 'E' },
  { img: imgF, label: 'F' },
  { img: imgG, label: 'G' },
  { img: imgH, label: 'H' },
  { img: imgI, label: 'I' },
  { img: imgJ, label: 'J' },
  { img: imgKM, label: 'K–M' },
  { img: imgNR, label: 'N–R' },
  { img: imgSZ, label: 'S–Z' },
  { img: imgFancy, label: 'Fancy' }
];

const GradingSystemsPage: React.FC = () => {
  return (
    <div className="grading-system-page">
      <GradientHero
        title={<><span>Grading Excellence</span><br/>You Can Trust</>}
        backTo="/"
        badgeText="GRADING SYSTEM"
        subtitleHighlight="Verified"
        subtitleRest=" Brilliance"
        lead={<>At SunDiam, every diamond is evaluated with meticulous precision through globally recognized grading standards. We primarily follow the GIA (Gemological Institute of America) system—renowned for its objectivity and consistency—while also aligning with respected laboratories like IGI and HRD. Each stone is assessed for its Cut, Clarity, Color, and Carat Weight, ensuring complete transparency and lasting confidence in every gem we offer.</>}
        className="grading-system-hero"
      />

      <section className="grading-overview-section">
        <Container>
          <h2 className="section-title">Diamond <span className="section-title-highlight">Shape</span></h2>
          <p >Shape is a key element in selecting the perfect diamond. At SunDiam, we craft a wide array of shapes—from princess, pear, and oval to heart, cushion, marquise, and emerald—each with its own unique character and brilliance, designed to match diverse styles and preferences.</p>

          <h2 className="section-title mt-5">Diamond <span className="section-title-highlight">Pricing</span> Guidelines We Follow:</h2>
          <div className="table-scroll-wrapper sticky-first-col size-range-wrapper" aria-label="Diamond size range reference table">
            <table className="grading-table size-range-table">
              <thead>
                <tr>
                  <th scope="col" className="first-col">Size</th>
                  {[
                    '0.30+', '0.40+', '0.50+', '0.70+', '0.90+', '1.00+',
                    '1.25+', '1.50+', '1.75+', '2.00+', '2.50+', '3.00+', '5.00+', '10.00+'
                  ].map(h => (<th key={h} scope="col">{h}</th>))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row" className="first-col">Size range</th>
                  {[
                    '0.30 to 0.399',
                    '0.40 to 0.499',
                    '0.50 to 0.699',
                    '0.70 to 0.899',
                    '0.90 to 0.999',
                    '1.00 to 1.249',
                    '1.25 to 1.499',
                    '1.50 to 1.749',
                    '1.750 to 1.999',
                    '2.00 to 2.499',
                    '2.50 to 2.999',
                    '3.00 to 4.999',
                    '5.00 to 9.999',
                    '10.00 to 29.999'
                  ].map(r => (<td key={r}>{r}</td>))}
                </tr>
              </tbody>
            </table>
          </div>
        </Container>
      </section>

      {/* Clarity (text left, table right) */}
      <section className="grading-section clarity-section" id="clarity">
        <Container>
          <h3 className="grading-subtitle">CLARITY:</h3>
          <div className="section-row">
            <div className="section-text">
              <p>Clarity refers to the presence of natural inclusions or blemishes within a diamond. These features are part of the diamond’s formation process deep within the Earth.</p>
              <p>Inclusions are internal characteristics, while blemishes appear on the surface. Both can impact a diamond’s brilliance and overall visual appeal.</p>
              <p>A flawless diamond has no visible imperfections under 10x magnification. Such stones are incredibly rare and command a high value in the market.</p>
              <p>Most diamonds have minor inclusions, but when well-cut, these flaws are often invisible to the naked eye and do not affect the stone’s beauty.</p>
              <p>Clarity grading helps buyers understand a diamond’s purity. The fewer the inclusions, the higher the clarity grade and value.</p>
              <p>At SunDiam, we grade diamonds using globally recognized standards, mainly following GIA guidelines. Most of our diamonds fall within the VS2 and above category, offering exceptional brilliance and lasting quality.</p>
            </div>
            <div className="section-table-wrapper">
              <table className="grading-table clarity-table vertical-two-col" aria-label="Diamond clarity grades">
                <thead><tr><th scope="col">Purity</th><th scope="col">Description</th></tr></thead>
                <tbody>
                  {CLARITY_ROWS.map(r => <tr key={r.code}><th scope="row">{r.code}</th><td>{r.desc}</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </Container>
      </section>

      {/* Color (table left, text right to alternate) */}
      <section className="grading-section color-section" id="color">
        <Container>
          <h3 className="grading-subtitle">COLOR:</h3>
          <div className="section-row">
            <div className="section-table-wrapper narrow">
              <table className="grading-table vertical-two-col" aria-label="Diamond color grades">
                <thead><tr><th scope="col">Color Grade</th><th scope="col">Description</th></tr></thead>
                <tbody>
                  {COLOR_ROWS.map(r => <tr key={r.code}><th scope="row">{r.code}</th><td>{r.desc}</td></tr>)}
                </tbody>
              </table>
            </div>
            <div className="section-text">
              <p>Diamonds that are close to being colorless are considered more rare and valuable. The less color a diamond has, the more light it can reflect, resulting in exceptional brilliance and sparkle. Grading is based on a color scale that begins with D (completely colorless) and ends at Z (light yellow or brown). Beyond this scale, some diamonds occur in vivid natural colors—such as red, blue, green, and pink—making them exceptionally rare and highly sought after.</p>
              <div className="color-image-grid" aria-label="Color grade image examples">
                {colorImageGroups.map(g => (
                  <figure key={g.label} className="color-grade-figure">
                    <img src={g.img} alt={`Color grade ${g.label}`} />
                    <figcaption>{g.label}</figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Cut (text left, table right) */}
      <section className="grading-section cut-section" id="cut">
        <Container>
          <h3 className="grading-subtitle">CUT:</h3>
          <p className="grading-paragraph cut-intro-text">
            The cut of a diamond defines its sparkle, brilliance, and overall beauty. It reflects the skill and precision of the cutter. Even a diamond with perfect color and clarity can appear dull if poorly cut. A well-executed cut maximizes light reflection and enhances the gem’s fire.
          </p>
          <div className="section-row grading-split-row">
            <div className="section-text grading-copy-col">
              <p>At SunDiam, we evaluate cut quality based on globally accepted grading standards, categorized as follows:</p>
            </div>
            <div className="section-table-wrapper grading-data-col">
              <MatrixTable title="Cut Grades" grades={CUT_MATRIX} />
            </div>
          </div>
        </Container>
      </section>

      {/* Polish (table left to alternate) */}
      <section className="grading-section polish-section" id="polish">
        <Container>
          <h3 className="grading-subtitle">POLISH:</h3>
          <p className="grading-paragraph split-intro-text">
            Polish refers to the smoothness and finish of a diamond’s facets, achieved in the final stage of the cutting process. A well-polished diamond allows light to reflect more clearly, enhancing its brilliance and overall appearance.
          </p>
          <div className="section-row grading-split-row">
            <div className="section-text grading-copy-col">
              <p>At SunDiam, diamonds are graded for polish quality using the following categories:</p>
            </div>
            <div className="section-table-wrapper grading-data-col">
              <MatrixTable title="Polish" grades={POLISH_MATRIX} />
            </div>
          </div>
        </Container>
      </section>

      {/* Symmetry (text left) */}
      <section className="grading-section symmetry-section" id="symmetry">
        <Container>
          <h3 className="grading-subtitle">SYMMETRY:</h3>
          <p className="grading-paragraph split-intro-text">
            Symmetry refers to the precise alignment and proportion of a diamond’s facets. For optimal brilliance, each facet must be evenly sized and accurately positioned opposite its counterpart. Poor symmetry can misdirect light, reducing the diamond’s sparkle.
          </p>
          <div className="section-row grading-split-row">
            <div className="section-text grading-copy-col">
              <p>We evaluate diamond symmetry using standardized grading categories.</p>
            </div>
            <div className="section-table-wrapper grading-data-col">
              <MatrixTable title="Symmetry" grades={SYMMETRY_MATRIX} />
            </div>
          </div>
        </Container>
      </section>

      {/* Heart & Arrows (stacked: text then table) */}
      <section className="grading-section hearts-arrows-section" id="heart-arrows">
        <Container>
          <h3 className="grading-subtitle">HEART & ARROWS:</h3>
          <div className="section-text full-width">
            <p>A flawless round diamond with ideal proportions is known as a ‘Heart and Arrows’ (H&A) diamond. Graded with advanced precision, H&A stones surpass standard cut grades, offering brilliance beyond the typical excellent cut.</p>
          </div>
          <div className="section-table-wrapper">
            <MatrixTable title="H&A Grades" grades={HA_MATRIX} />
          </div>
        </Container>
      </section>

      {/* Luster (two tables stacked as per image: content then two tables side by side) */}
      <section className="grading-section luster-section" id="luster">
        <Container>
          <h3 className="grading-subtitle">LUSTER:</h3>
          <div className="section-text full-width">
            <p>Luster and brilliance are among the most prized qualities of a diamond. Luster refers to its sparkle and fire, while milkiness is a whitish or greyish haze caused by internal inclusions that can dull its shine. Based on the light reflected, diamonds are graded across three levels of luster and three levels of milkiness.</p>
          </div>
          <h5 className="mini-heading">Level of Brilliance:</h5>
          <div className="dual-table-wrapper">
            <MatrixTable title="Luster Grades" grades={LUSTER_MATRIX} />
          </div>
          <h5 className="mini-heading mt-4">Level of Milkiness:</h5>
          <div className="dual-table-wrapper">
            <MatrixTable title="Milky Grades" grades={MILKY_MATRIX} />
          </div>
        </Container>
      </section>

      {/* Fluorescence (stacked: text then table) */}
      <section className="grading-section fluorescence-section" id="fluorescence">
        <Container>
          <h3 className="grading-subtitle">FLUORESCENCE:</h3>
          <div className="section-text full-width">
            <p>When exposed to ultraviolet (UV) light, certain diamonds emit a visible glow—typically blue—due to natural fluorescence. This reaction varies in intensity and is most commonly observed under black light. Diamonds are graded based on the strength of their fluorescence.</p>
          </div>
          <div className="section-table-wrapper">
            <MatrixTable title="Fluorescence" grades={FLUORESCENCE_MATRIX} />
          </div>
        </Container>
      </section>

      {/* White Inclusion (stacked content + full-width table) */}
      <section className="grading-section white-inclusion-section" id="white-inclusion">
        <Container>
          <h3 className="grading-subtitle">WHITE INCLUSION:</h3>
          <div className="section-text full-width">
            <p>At SunDiam, diamonds are graded based on five primary types of inclusions: Pin Points (PP), Clouds (CLD), Crystals (CRL), Feathers (FR), and Spreadead (SPR).</p>
            <ul className="bullet-list">
              <li><strong>Pin Point (PP):</strong> Tiny mineral crystals that appear as small dots under 10x magnification. They are usually white but can also appear dark.</li>
              <li><strong>Clouds (CLD):</strong> Clusters of closely grouped pin points. Some are dense and invisible to the naked eye, while others resemble a faint haze.</li>
              <li><strong>Crystal (CRL):</strong> Solid mineral inclusions found within diamonds, occurring singly or in clusters, and varying in size and color.</li>
              <li><strong>Feather (FR):</strong> A common term for internal fractures or breaks within a diamond.</li>
              <li><strong>Spreadead (SPR):</strong> A condition where feather inclusions are numerous and dispersed throughout the diamond, rather than localized.</li>
            </ul>
          </div>
          <div className="section-table-wrapper">
            <MatrixTable title="Other Inclusion" grades={WHITE_INCLUSION_MATRIX} descLabel="Size range" />
          </div>
          <div className="section-text full-width">
            <p>SunDiam Classifies White Inclusions into Two Categories:</p>
            <p className="inline-cats">• Table White Inclusions &nbsp; • Side White Inclusions</p>
          </div>
        </Container>
      </section>

      {/* Black Inclusion (text left) */}
      <section className="grading-section black-inclusion-section" id="black-inclusion">
        <Container>
          <h3 className="grading-subtitle">BLAK INCLUSION (Natts):</h3>
          <div className="section-row">
            <div className="section-text">
              <p>Diamonds are graded for black inclusions based on their proportion relative to total inclusions, categorized as follows:</p>
            <div className="section-table-wrapper">
            <MatrixTable title="Black Inclusion" grades={BLACK_INCLUSION_MATRIX} />
            </div>
            
              <p className="mt-3">Black inclusions are classified into two types based on their position within the diamond:</p>
              <p className="inline-cats">• Table Black Inclusions &nbsp; • Side Black Inclusions</p>
            </div>
          </div>
        </Container>
      </section>

      {/* Open Inclusion (stacked: text then table then bullets) */}
      <section className="grading-section open-inclusion-section" id="open-inclusion">
        <Container>
          <h3 className="grading-subtitle">OPEN INCLUSION:</h3>
          <div className="section-text full-width">
            <p>An open inclusion refers to a minor surface-reaching feature typically located near the girdle, culet, or facet junctions of a diamond.</p>
            <p>Diamonds containing such inclusions are graded accordingly under the following classifications:</p>
          </div>
          <div className="section-table-wrapper">
            <MatrixTable title="Open Inclusion" grades={OPEN_INCLUSION_MATRIX} />
          </div>
          <div className="section-text full-width">
            <p>We classify open inclusions based on four key areas commonly used in diamond grading practices:</p>
            <p className="inline-cats">• Table Open Inclusion &nbsp; • Crown Open Inclusion</p>
            <p className="inline-cats">• Pavilion Open Inclusion &nbsp; • Girdle Open Inclusion</p>
          </div>
        </Container>
      </section>

      {/* Extra Facet (stacked: text then table then bullets) */}
      <section className="grading-section extra-facet-section" id="extra-facet">
        <Container>
          <h3 className="grading-subtitle">EXTRA FACET:</h3>
          <div className="section-text full-width">
            <p>An extra facet refers to an additional surface intentionally added beyond the standard facet arrangement of a brilliant cut diamond, often without regard for the stone’s overall symmetry.</p>
            <p>We categorized Extra facets into three sections:</p>
          </div>
          <div className="section-table-wrapper">
            <MatrixTable title="Extra Facet" grades={EXTRA_FACET_MATRIX} />
          </div>
          <div className="section-text full-width">
            <p className="inline-cats">• Table Extra Facet &nbsp; • Crown Extra Facet</p>
            <p className="inline-cats">• Pavilion Extra Facet</p>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default GradingSystemsPage;
